/**
 * Date-only helpers for `YYYY-MM-DD` strings (occasions and birthdays have no time).
 * All math is done in UTC on the calendar date, so timezones and DST can't shift a day.
 */

const toUtc = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
};

const DAY = 86_400_000;

/** Whole days from `from` to `to` (negative if `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY);
}

/** Today's date as YYYY-MM-DD in the given IANA timezone (default: the device's). */
export function todayIso(timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** "Fri, Dec 25" (or "Fri, Dec 25, 2027" when not in `today`'s year). */
export function formatAbsolute(date: string, today: string, locale?: string): string {
  const sameYear = date.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    timeZone: 'UTC',
  }).format(new Date(toUtc(date)));
}

/** "Dec 25" */
export function formatShort(date: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(toUtc(date)));
}

/**
 * The one relative-date vocabulary (docs/05 › Global patterns):
 * "today!", "tomorrow", "in 5 days", "in 3 weeks", beyond 60 days "Dec 25"; and the past equivalents.
 */
export function relativeDate(date: string, today: string, locale?: string): string {
  const n = daysBetween(today, date);
  if (n === 0) return 'today!';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  if (n > 1 && n < 14) return `in ${n} days`;
  if (n >= 14 && n <= 60) return `in ${Math.round(n / 7)} weeks`;
  if (n < -1 && n > -14) return `${-n} days ago`;
  if (n <= -14 && n >= -60) return `${Math.round(-n / 7)} weeks ago`;
  return formatShort(date, locale);
}
